import { FanguardApiContractsClientAssetModel } from 'src/api/generated';
import { sanitizeFileName } from 'src/utils/sanitize';

export type AssetType = NonNullable<FanguardApiContractsClientAssetModel['type']>;

export const ASSET_TYPES: AssetType[] = ['image', 'animation', 'font'];

export interface AssetFormValue {
  id?: string;
  name: string;
  file: string;
  contentType: string;
  type?: AssetType | null;
}

// The empty upload slot and an in-flight upload have no type yet, so they stay visible under every filter.
export const isAssetVisibleForType = (asset: AssetFormValue, typeFilter: AssetType | null): boolean =>
  !typeFilter || !asset.id || asset.id === 'loading' || asset.type === typeFilter;

export interface FileData {
  url?: string;
  image?: string;
  name?: string;
  size?: number | string;
}

export const getContentTypeFromDataUrl = (dataUrl: string): string => {
  if (!dataUrl) return '';
  const match = dataUrl.match(/^data:([a-zA-Z0-9-+./]+);base64,/);
  return match ? match[1] : '';
};

export const stripDataUrlPrefix = (dataUrl: string): string => {
  const match = dataUrl.match(/^data:[^;]+;base64,(.*)$/);
  return match ? match[1] : dataUrl;
};

export const ensureSingleEmptySlot = (assets: AssetFormValue[]): AssetFormValue[] => {
  const result = [...assets];

  const nonEmptyAssets = result.filter((asset) => asset.file || asset.name);

  nonEmptyAssets.push({ name: '', file: '', contentType: '', id: '' });

  return nonEmptyAssets;
};

export const checkForDuplicateAsset = (
  fileData: FileData | string | null,
  existingAssets: AssetFormValue[],
): boolean => {
  if (!fileData) return false;

  if (typeof fileData === 'object' && fileData.name) {
    const fileName = sanitizeFileName(fileData.name);
    const duplicateByName = existingAssets.some(
      (asset) => asset.name && asset.name.toLowerCase() === fileName.toLowerCase() && asset.id !== 'loading',
    );
    if (duplicateByName) return true;
  }

  let fileContent = '';
  if (typeof fileData === 'object') {
    fileContent = stripDataUrlPrefix(fileData.url || fileData.image || '');
  } else if (typeof fileData === 'string') {
    fileContent = stripDataUrlPrefix(fileData);
  }

  if (fileContent) {
    const duplicateByContent = existingAssets.some(
      (asset) => asset.file && asset.file === fileContent && asset.id !== 'loading',
    );
    if (duplicateByContent) return true;
  }

  return false;
};

export const mapApiAssetsToFormValues = (
  apiAssets: Array<{
    id?: string | null;
    name?: string | null;
    url?: string | null;
    contentType?: string | null;
    type?: AssetType | null;
  }>,
): AssetFormValue[] => {
  const mapped = apiAssets.map((a) => ({
    name: sanitizeFileName(a.name || ''),
    type: a.type ?? null,
    file: a.url || '',
    contentType:
      typeof (a as { contentType: string }).contentType === 'string' && (a as { contentType: string }).contentType
        ? (a as { contentType: string }).contentType
        : getContentTypeFromDataUrl(a.url || ''),
    id: a.id || '',
  }));

  return ensureSingleEmptySlot(mapped);
};
